// Tatmeen/SSCCAggregation/aggregationSlice.js
import { createSlice } from "@reduxjs/toolkit";
import { createCSSSCC, fetchPurchaseVouchers, fetchAvailableSSCCCodes, createAggregation, fetchUnpackStatus, unpackSSCC } from "./aggregationThunk";

const initialState = {
  // Purchase voucher data
  selectedPurchaseId: null,
  selectedPurchase: null,
  purchaseVouchers: [],
  masterSSCC: [],
  beSSCCs: [],
  csSSCCs: [],
  aggregations: [],
  hierarchyTree: [],
  unlinkedSSCCs: [],
  unpackSSCCList: [],
  selectedUnpackSSCC: null,
  // SSCC data
  ssccs: [],


  // Hierarchy (relationship-based)
  hierarchy: {
    nodes: [],
    edges: [],
    rootNodes: [],
    leafNodes: []
  },

  // Selection state
  selectedSSCCs: [],
  selectedParent: null,
  selectedChild: null,

  // UI state
  loading: false,
  error: null,
  isDrawerOpen: false,
  isConfirmModalOpen: false,
  isSaving: false,

  // Filter and search
  searchTerm: '',
  filterGS1Level: 'all',
  filterStatus: 'all',

  // Statistics
  statistics: {
    totalSSCC: 0,
    rootSSCC: 0,
    aggregatedSSCC: 0,
    unAggregatedSSCC: 0
  },

  // Validation
  validationErrors: [],
  isHierarchyValid: false,

  // Unpacking
  unpackInProgress: false
};

const aggregationSlice = createSlice({
  name: 'ssccAggregation',
  initialState,
  reducers: {
    // Purchase selection
    setSelectedPurchaseId(state, action) {
      state.selectedPurchaseId = action.payload;
    },
    setSelectedPurchase(state, action) {
      state.selectedPurchase = action.payload;
    },
    setPurchaseVouchers(state, action) {
      state.purchaseVouchers = action.payload;
    },
    // SSCC data
    setSSCCs(state, action) {
      state.ssccs = action.payload;
    },
    setMasterSSCC(state, action) {
      state.masterSSCC = action.payload || [];
    },

    setBeSSCCs(state, action) {
      state.beSSCCs = action.payload || [];
    },

    setCsSSCCs(state, action) {
      state.csSSCCs = action.payload || [];
    },
    setAggregations(
      state,
      action
    ) {
      state.aggregations =
        action.payload || [];
    },
    // Hierarchy
    setHierarchy(state, action) {
      state.hierarchy = action.payload;
    },
    setHierarchyNodes(state, action) {
      state.hierarchy.nodes = action.payload;
    },
    setHierarchyEdges(state, action) {
      state.hierarchy.edges = action.payload;
    },
    setRootNodes(state, action) {
      state.hierarchy.rootNodes = action.payload;
    },
    setLeafNodes(state, action) {
      state.hierarchy.leafNodes = action.payload;
    },

    // Selection
    setSelectedSSCCs(state, action) {
      state.selectedSSCCs = action.payload;
    },
    addSelectedSSCC(state, action) {
      if (!state.selectedSSCCs.includes(action.payload)) {
        state.selectedSSCCs.push(action.payload);
      }
    },
    removeSelectedSSCC(state, action) {
      state.selectedSSCCs = state.selectedSSCCs.filter(
        id => id !== action.payload
      );
    },
    clearSelectedSSCCs(state) {
      state.selectedSSCCs = [];
    },
    setSelectedParent(state, action) {
      state.selectedParent = action.payload;
    },
    setSelectedChild(state, action) {
      state.selectedChild = action.payload;
    },

    // UI state
    setLoading(state, action) {
      state.loading = action.payload;
    },
    setIsSaving(state, action) {
      state.isSaving = action.payload;
    },
    setError(state, action) {
      state.error = action.payload;
    },
    setDrawerOpen(state, action) {
      state.isDrawerOpen = action.payload;
    },
    setConfirmModalOpen(state, action) {
      state.isConfirmModalOpen = action.payload;
    },
    setUnpackSSCCList(state, action) {
      state.unpackSSCCList =
        action.payload || [];
    },

    setSelectedUnpackSSCC(state, action) {
      state.selectedUnpackSSCC =
        action.payload;
    },
    // Filter and search
    setSearchTerm(state, action) {
      state.searchTerm = action.payload;
    },
    setFilterGS1Level(state, action) {
      state.filterGS1Level = action.payload;
    },
    setFilterStatus(state, action) {
      state.filterStatus = action.payload;
    },
    setAggregations(state, action) {
      state.aggregations = action.payload || [];
    },
    setHierarchyTree(state, action) {
      state.hierarchyTree =
        action.payload || [];
    },
    setUnlinkedSSCCs(state, action) {
      state.unlinkedSSCCs =
        action.payload || [];
    },
    // Statistics
    setStatistics(state, action) {
      state.statistics = action.payload;
    },
    updateStatistics(state) {
      const nodes = state.hierarchy.nodes || [];
      const edges = state.hierarchy.edges || [];

      // Find nodes with no parent (roots)
      const hasParent = new Set(edges.map(e => e.target));
      const rootNodes = nodes.filter(n => !hasParent.has(n.id));

      // Find all participating nodes in aggregation
      const participating = new Set();
      edges.forEach(edge => {
        participating.add(edge.source);
        participating.add(edge.target);
      });

      const stats = {
        totalSSCC: nodes.length,
        rootSSCC: rootNodes.length,
        aggregatedSSCC: participating.size,
        unAggregatedSSCC: nodes.length - participating.size
      };

      state.statistics = stats;
    },

    // Validation
    setValidationErrors(state, action) {
      state.validationErrors = action.payload;
    },
    setIsHierarchyValid(state, action) {
      state.isHierarchyValid = action.payload;
    },

    // Unpacking
    setUnpackInProgress(state, action) {
      state.unpackInProgress = action.payload;
    },

    // Reset
    resetAggregation(state) {
      return { ...initialState };
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPurchaseVouchers.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPurchaseVouchers.fulfilled, (state, action) => {
        state.loading = false;
        state.purchaseVouchers = action.payload || [];
      })
      .addCase(fetchPurchaseVouchers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error?.message || "Failed to load vouchers";
      })
      .addCase(
        fetchUnpackStatus.pending,
        state => {
          state.loading = true;
        }
      )
      .addCase(
        fetchUnpackStatus.fulfilled,
        (state, action) => {
          state.loading = false;
          state.unpackSSCCList =
            action.payload || [];
        }
      )
      .addCase(
        fetchUnpackStatus.rejected,
        (state, action) => {
          state.loading = false;
          state.error =
            action.payload ||
            "Failed to load unpack status";
        }
      )
      .addCase(
        unpackSSCC.pending,
        state => {
          state.unpackInProgress = true;
        }
      )
      .addCase(
        unpackSSCC.fulfilled,
        state => {
          state.unpackInProgress = false;
        }
      )
      .addCase(
        unpackSSCC.rejected,
        (state, action) => {
          state.unpackInProgress = false;
          state.error = action.payload;
        }
      )
  }
});

export const {
  setSelectedPurchaseId,
  setSelectedPurchase,
  setPurchaseVouchers,
  setSSCCs,
  setMasterSSCC,
  setBeSSCCs,
  setCsSSCCs,
  setHierarchy,
  setHierarchyNodes,
  setHierarchyEdges,
  setRootNodes,
  setLeafNodes,
  setSelectedSSCCs,
  addSelectedSSCC,
  removeSelectedSSCC,
  clearSelectedSSCCs,
  setSelectedParent,
  setSelectedChild,
  setLoading,
  setIsSaving,
  setError,
  setDrawerOpen,
  setConfirmModalOpen,
  setSearchTerm,
  setFilterGS1Level,
  setFilterStatus,
  setStatistics,
  updateStatistics,
  setValidationErrors,
  setIsHierarchyValid,
  setUnpackInProgress,
  setAggregations,
  setHierarchyTree,
  setUnlinkedSSCCs,
  resetAggregation,
  setUnpackSSCCList,

  setSelectedUnpackSSCC,
} = aggregationSlice.actions;

export default aggregationSlice.reducer;